import { useId } from 'react'
import { Field, FieldGrid, TextInput, CodeSelect, toLocalInput, fromLocalInput } from './fields'
import type { IncidentVocabulary } from '../api'

/**
 * Step 1 · Classification — what happened, and when.
 *
 * The only step that cannot be skipped: `category` and `title` are what `POST /incidents`
 * needs to create the draft row at all, and until that row exists there is nothing to
 * attach evidence to and nothing to resume.
 *
 * `subcategory` is free text with a datalist of values already in use. A closed list would
 * mean a field reporter watching something the taxonomy does not name has nowhere to put
 * it, and "OTHER" with no words after it is the same as no report.
 */
export function StepClassification({
  vocab, value, onChange, errors, knownSubcategories = [],
}: {
  vocab: IncidentVocabulary | null
  value: {
    categoryCode: string | null
    subcategory: string | null
    title: string
    occurredAt: string | null
    occurredAtPrecisionCode: string | null
  }
  onChange: (patch: Partial<typeof value>) => void
  errors: Record<string, string>
  knownSubcategories?: string[]
}) {
  const ids = useId()
  const listId = `${ids}-subcats`

  return (
    <div className="space-y-4">
      <FieldGrid>
        <Field label="Category" htmlFor={`${ids}-cat`} error={errors.categoryCode ?? errors.category}
          hint="Required. The broad kind of thing this is.">
          <CodeSelect id={`${ids}-cat`} codes={vocab?.categories} value={value.categoryCode}
            onChange={(v) => onChange({ categoryCode: v })} emptyLabel="Choose a category…" />
        </Field>

        <Field label="Subcategory" htmlFor={`${ids}-sub`} error={errors.subcategory}
          hint="Optional, free text. Suggestions come from reports already filed.">
          <TextInput id={`${ids}-sub`} list={listId} max={120}
            value={value.subcategory ?? ''}
            onChange={(v) => onChange({ subcategory: v || null })}
            placeholder="e.g. Stone-throwing at a queue" />
          <datalist id={listId}>
            {knownSubcategories.map((s) => <option key={s} value={s} />)}
          </datalist>
        </Field>
      </FieldGrid>

      <Field label="Title" htmlFor={`${ids}-title`} error={errors.title}
        hint="Required. One line a desk can scan — what happened, and where if it fits.">
        <TextInput id={`${ids}-title`} max={200} value={value.title}
          onChange={(v) => onChange({ title: v })}
          placeholder="Crowd blocked the gate at the tally centre" />
      </Field>

      <FieldGrid>
        <Field label="When it happened" htmlFor={`${ids}-when`} error={errors.occurredAt}
          hint="Leave empty if you do not know. Reported-at is recorded either way.">
          <TextInput id={`${ids}-when`} type="datetime-local"
            value={toLocalInput(value.occurredAt)}
            onChange={(v) => onChange({ occurredAt: fromLocalInput(v) })} />
        </Field>

        <Field label="How precisely you know that"
          htmlFor={`${ids}-prec`}
          error={errors.occurredAtPrecisionCode}
          hint="“Some time that afternoon” is a real answer. Say so rather than inventing a minute.">
          <CodeSelect id={`${ids}-prec`} codes={vocab?.timePrecisions}
            value={value.occurredAtPrecisionCode}
            onChange={(v) => onChange({ occurredAtPrecisionCode: v })}
            emptyLabel="To the minute (default)" />
        </Field>
      </FieldGrid>
    </div>
  )
}
